import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupplierInvoices, SupplierInvoice } from '../../supplier-invoices/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface RecentSupplierInvoicesProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentSupplierInvoices: React.FC<RecentSupplierInvoicesProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { data: invoices = [], isLoading } = useSupplierInvoices();
  const arr = Array.isArray(invoices) ? invoices : [];

  const items = React.useMemo(() => {
    return [...arr]
      .sort((a: SupplierInvoice, b: SupplierInvoice) => {
        const da = new Date(a.invoiceDate || a.dueDate || a.createdAt || 0).getTime();
        const db = new Date(b.invoiceDate || b.dueDate || b.createdAt || 0).getTime();
        return db - da;
      })
      .slice(0, effectiveLimit);
  }, [arr, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  const formatCurrency = (v?: number | null) =>
    v != null ? `€${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

  return (
    <GenericListWidget<SupplierInvoice>
      title="Recent Supplier Invoices"
      viewAllPath="/supplier-invoices"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No supplier invoices"
      getItemKey={(inv) => inv.id}
      embedded={embedded}
      renderItem={(inv) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/supplier-invoices/${inv.id}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/supplier-invoices/${inv.id}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {inv.supplier?.name || `Supplier #${inv.supplierId}`}
              {inv.invoiceNo && ` · ${inv.invoiceNo}`}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {formatCurrency(inv.totalOrIncome ?? inv.amount)}
              {inv.dueDate &&
                ` · Due ${new Date(inv.dueDate).toLocaleDateString('en-US')}`}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentSupplierInvoices;
